// =============================================================================
// EditSpaceDialog - Dialog d'édition d'un espace
// =============================================================================

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Folder, Loader2, Trash2, Globe, Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useSpacesStore } from '@/modules/project/hooks/useSpacesStore';
import type { Space } from '@/modules/project/data/spaceRepository';

// Couleurs prédéfinies pour les espaces
const SPACE_COLORS = [
  '#6366F1', '#8B5CF6', '#EC4899', '#EF4444', '#F97316',
  '#EAB308', '#22C55E', '#14B8A6', '#06B6D4', '#3B82F6',
];

interface EditSpaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  space: Space | null;
}

export function EditSpaceDialog({ open, onOpenChange, space }: EditSpaceDialogProps) {
  const { editSpace, removeSpace } = useSpacesStore();
  
  const isArchiveSpace = space?.is_archive ?? false;
  
  const [name, setName] = useState('');
  const [color, setColor] = useState(SPACE_COLORS[0]);
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [visibilityConfirmOpen, setVisibilityConfirmOpen] = useState(false);
  const [pendingVisibility, setPendingVisibility] = useState<'public' | 'private' | null>(null);

  useEffect(() => {
    if (space) {
      setName(space.name);
      setColor(space.color || SPACE_COLORS[0]);
      setVisibility(space.visibility || 'public');
    }
  }, [space]);

  const handleVisibilityToggle = (checked: boolean) => {
    const newVisibility = checked ? 'private' : 'public';
    setPendingVisibility(newVisibility);
    setVisibilityConfirmOpen(true);
  };

  const confirmVisibilityChange = () => {
    if (pendingVisibility) {
      setVisibility(pendingVisibility);
    }
    setVisibilityConfirmOpen(false);
    setPendingVisibility(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!space) return;
    if (space.is_archive) {
      toast.error("L'espace d'archives ne peut pas être modifié");
      return;
    }
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Le nom de l'espace doit contenir au moins 2 caractères");
      return;
    }
    if (name.trim().length > 60) {
      toast.error("Le nom ne peut pas dépasser 60 caractères");
      return;
    }

    setIsLoading(true);
    try {
      await editSpace(space.id, { name: name.trim(), color, visibility });
      toast.success('Espace modifié avec succès');
      onOpenChange(false);
    } catch (err: any) {
      console.error('Error updating space:', err);
      toast.error(err.message || "Erreur lors de la modification de l'espace");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!space) return;
    setIsLoading(true);
    try {
      await removeSpace(space.id);
      toast.success('Espace supprimé avec succès');
      setDeleteDialogOpen(false);
      onOpenChange(false);
    } catch (err: any) {
      console.error('Error deleting space:', err);
      toast.error(err.message || "Erreur lors de la suppression de l'espace");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) onOpenChange(false);
  };

  if (!space) return null;

  if (isArchiveSpace) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[360px] p-0 gap-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border/60">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center h-7 w-7 rounded-md"
                style={{ backgroundColor: space.color || '#78716c' }}>
                <Folder className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-[13px] font-semibold leading-tight">Espace d'archives</DialogTitle>
                <DialogDescription className="text-[11px] text-muted-foreground leading-tight mt-0.5">Espace système</DialogDescription>
              </div>
            </div>
          </div>
          <div className="px-4 py-3">
            <p className="text-[12px] text-muted-foreground leading-relaxed">
              L'espace d'archives est un espace système qui ne peut être ni modifié, ni renommé, ni supprimé.
              Les projets sont automatiquement déplacés vers cet espace lorsqu'ils sont archivés.
            </p>
          </div>
          <div className="px-4 py-2.5 border-t border-border/60 flex justify-end">
            <Button size="sm" className="h-7 text-[12px] px-3" onClick={() => onOpenChange(false)}>Fermer</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const hasChanges = name.trim() !== space?.name || color !== (space?.color || SPACE_COLORS[0]) || visibility !== (space?.visibility || 'public');

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[360px] p-0 gap-0 overflow-hidden">
          {/* Header compact */}
          <div className="px-4 py-3 border-b border-border/60">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center h-7 w-7 rounded-md transition-colors"
                style={{ backgroundColor: color }}>
                <Folder className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-[13px] font-semibold leading-tight">Modifier l'espace</DialogTitle>
                <DialogDescription className="text-[11px] text-muted-foreground leading-tight mt-0.5">Nom, couleur et visibilité</DialogDescription>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-4 py-3 space-y-3">
              {/* Nom */}
              <div className="space-y-1">
                <Label htmlFor="space-name" className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Nom</Label>
                <Input id="space-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Marketing" required minLength={2} disabled={isLoading}
                  className="h-8 text-[13px]" />
              </div>

              {/* Couleur */}
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Couleur</Label>
                <div className="flex flex-wrap gap-1.5">
                  {SPACE_COLORS.map((c) => (
                    <button key={c} type="button" 
                      className="w-6 h-6 rounded-md transition-all hover:scale-110"
                      style={{
                        backgroundColor: c,
                        outline: color === c ? '2px solid' : 'none',
                        outlineColor: c, outlineOffset: '2px',
                      }}
                      onClick={() => setColor(c)} disabled={isLoading} />
                  ))}
                </div>
              </div>

              {/* Visibilité */}
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Visibilité</Label>
                <div className="flex items-center justify-between px-2.5 py-2 border border-border/60 rounded-md bg-muted/30">
                  <div className="flex items-center gap-2">
                    {visibility === 'private' ? (
                      <Lock className="h-3.5 w-3.5 text-amber-500" />
                    ) : (
                      <Globe className="h-3.5 w-3.5 text-emerald-500" />
                    )}
                    <div>
                      <p className="text-[12px] font-medium leading-tight">
                        {visibility === 'private' ? 'Privé' : 'Public'}
                      </p>
                      <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                        {visibility === 'private' 
                          ? 'Sur invitation uniquement'
                          : 'Tous les collaborateurs'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={visibility === 'private'}
                    onCheckedChange={handleVisibilityToggle}
                    disabled={isLoading}
                    className="scale-90"
                  />
                </div>
              </div>
            </div>

            {/* Footer compact */}
            <div className="px-4 py-2.5 border-t border-border/60 flex items-center justify-between">
              <div>
                {!space.is_default && !space.is_archive && (
                  <button type="button" onClick={() => setDeleteDialogOpen(true)} disabled={isLoading}
                    className="text-[11px] text-destructive hover:text-destructive/80 font-medium flex items-center gap-1 transition-colors">
                    <Trash2 className="h-3 w-3" />Supprimer
                  </button>
                )}
              </div>
              <div className="flex gap-1.5">
                <Button type="button" variant="ghost" size="sm" onClick={handleClose} disabled={isLoading}
                  className="h-7 text-[12px] px-2.5">Annuler</Button>
                <Button 
                  type="submit" 
                  size="sm"
                  disabled={isLoading || !name.trim() || !hasChanges}
                  className="h-7 text-[12px] px-3 text-white"
                  style={{ 
                    backgroundColor: hasChanges ? color : undefined,
                    opacity: !hasChanges ? 0.5 : 1,
                  }}
                >
                  {isLoading ? (<><Loader2 className="mr-1.5 h-3 w-3 animate-spin" />...</>) : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Visibility Confirmation Dialog */}
      <AlertDialog open={visibilityConfirmOpen} onOpenChange={setVisibilityConfirmOpen}>
        <AlertDialogContent className="sm:max-w-[340px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[14px]">
              {pendingVisibility === 'private' 
                ? 'Rendre l\'espace privé ?'
                : 'Rendre l\'espace public ?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[12px]">
              {pendingVisibility === 'private'
                ? 'Les collaborateurs sans invitation explicite ne verront plus cet espace ni ses projets publics.'
                : 'Tous les collaborateurs du workspace pourront voir cet espace et ses projets publics.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingVisibility(null)} className="h-7 text-[12px]">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmVisibilityChange} className="h-7 text-[12px]">
              {pendingVisibility === 'private' ? 'Rendre privé' : 'Rendre public'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="sm:max-w-[340px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[14px]">Supprimer l'espace</AlertDialogTitle>
            <AlertDialogDescription className="text-[12px]">
              Supprimer "{space.name}" ? Les projets seront déplacés vers l'espace par défaut. Action irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading} className="h-7 text-[12px]">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 h-7 text-[12px]">
              {isLoading ? (<><Loader2 className="mr-1.5 h-3 w-3 animate-spin" />...</>) : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
